// iter176 — BFR "Send Email" quick modal, opened from the calendar tile.
//
// Flow:
//   1. Modal opens with the booking already selected (booking_id passed in).
//   2. Auto-detects the OTA from the booking (server-side filter).
//   3. Fetches templates from the booking-vault-admin panel via
//      GET /api/admin/email/templates (proxy with local seed fallback).
//   4. "Select Template" dropdown → auto-fills subject + body with the
//      booking's actual values via POST /api/admin/email/resolve.
//   5. User reviews / edits + hits Send.
//   6. Delivery routed through the existing Gmail bridge which uses the
//      SMTP credentials configured in the admin panel.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Loader2, Send, RefreshCw, Mail } from 'lucide-react';
import { toast } from 'sonner';

import { bfrEmailAPI } from '../services/api';

const SendEmailModal = ({ booking, onClose, onSent }) => {
  const bookingId = booking?.booking_id || '';
  const detectedOta = useMemo(
    () => (booking?.ota_source || booking?.ota_name || booking?.booking_source || '').trim(),
    [booking],
  );

  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [to, setTo] = useState(booking?.guest_email || booking?.email || '');
  // iter177 — Track whether the user has manually edited "To". Once they
  // do, we stop overriding it with the auto-fetched OTA support email.
  const [toManuallyEdited, setToManuallyEdited] = useState(false);
  const [toSource, setToSource] = useState('');   // 'ota_support' | 'guest' | ''
  // iter182 — Global CC/BCC pulled from booking-vault-admin; read-only
  // in the modal and always injected into the outgoing send payload.
  const [globalCc, setGlobalCc] = useState('');
  const [globalBcc, setGlobalBcc] = useState('');
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [sending, setSending] = useState(false);
  const [source, setSource] = useState('');   // 'admin_panel' | 'fallback_seed'

  // iter182 — Fetch org-wide CC/BCC on modal open (force refresh once so
  // the composer always shows the latest addresses configured by ops).
  useEffect(() => {
    let cancelled = false;
    bfrEmailAPI.globalSettings({ refresh: true })
      .then((res) => {
        if (cancelled) return;
        const cc = (res?.RESULT?.cc ?? res?.cc ?? '').trim();
        const bcc = (res?.RESULT?.bcc ?? res?.bcc ?? '').trim();
        setGlobalCc(cc);
        setGlobalBcc(bcc);
      })
      .catch(() => { /* silent — send still works, just without CC/BCC */ });
    return () => { cancelled = true; };
  }, []);

  // iter177 — Auto-fetch the OTA's partner/support email from the admin
  // panel and prefill "To". User can still edit; once edited we never
  // overwrite. Falls back to guest email if the panel has no mapping.
  useEffect(() => {
    if (!detectedOta && !bookingId) return;
    let cancelled = false;
    bfrEmailAPI.otaSupportEmail({ ota: detectedOta, booking_id: bookingId })
      .then((res) => {
        if (cancelled) return;
        const otaEmail = (res?.RESULT?.email ?? res?.email ?? '').trim();
        if (otaEmail && !toManuallyEdited) {
          setTo(otaEmail);
          setToSource('ota_support');
        }
      })
      .catch(() => { /* silent — modal already has guest email as default */ });
    return () => { cancelled = true; };
    // Only re-run when the booking/OTA context changes. Ignoring
    // toManuallyEdited on purpose so late-arriving responses respect a
    // subsequent edit (we recheck the flag inside .then()).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectedOta, bookingId]);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bfrEmailAPI.listTemplates(bookingId);
      const list = res?.RESULT?.templates ?? res?.templates ?? [];
      setTemplates(list);
      setSource(res?.RESULT?.source ?? res?.source ?? '');
      if (list.length && !selectedId) {
        // Preselect first template so the modal shows something useful.
        setSelectedId(list[0].id);
      }
    } catch (e) {
      toast.error('Could not load email templates.');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [bookingId, selectedId]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // When template selection changes, resolve variables from the booking.
  useEffect(() => {
    if (!selectedId) return;
    const tpl = templates.find((t) => t.id === selectedId);
    if (!tpl) return;
    let cancelled = false;
    setResolving(true);
    bfrEmailAPI.resolveTemplate({
      booking_id: bookingId,
      subject: tpl.subject || '',
      body: tpl.body || '',
    })
      .then((res) => {
        if (cancelled) return;
        setSubject(res?.RESULT?.subject ?? res?.subject ?? tpl.subject ?? '');
        setBody(res?.RESULT?.body ?? res?.body ?? tpl.body ?? '');
      })
      .catch(() => {
        if (cancelled) return;
        // Fall back to unresolved template — user can still send if needed.
        setSubject(tpl.subject || '');
        setBody(tpl.body || '');
      })
      .finally(() => { if (!cancelled) setResolving(false); });
    return () => { cancelled = true; };
  }, [selectedId, templates, bookingId]);

  const handleSend = async () => {
    if (!to.trim()) { toast.error('Recipient email is required.'); return; }
    if (!subject.trim()) { toast.error('Subject is required.'); return; }
    if (!body.trim()) { toast.error('Body is required.'); return; }
    setSending(true);
    try {
      await bfrEmailAPI.send({
        booking_id: bookingId,
        to: to.trim(),
        cc: globalCc || undefined,
        bcc: globalBcc || undefined,
        subject,
        body,
        auto_resolve: false,   // already resolved
        ota_name: detectedOta || undefined,
      });
      toast.success('Email sent.');
      onSent?.();
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Send failed.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="send-email-modal"
    >
      <div
        className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#1B2A7B] px-5 py-4 text-white flex items-center gap-3">
          <div className="w-9 h-9 bg-white/15 rounded-lg flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold text-sm truncate">Send Email — {booking?.guest_name || 'Guest'}</h2>
            <p className="text-[11px] text-white/80 truncate">
              Booking {bookingId}
              {detectedOta ? ` · OTA: ${detectedOta}` : ' · OTA: (unknown)'}
              {source === 'fallback_seed' ? ' · templates: local seed' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-white/15"
            aria-label="Close"
            data-testid="send-email-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Select Template</label>
            <div className="flex items-center gap-2">
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                disabled={loading || sending}
                className="flex-1 px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm focus:border-[#1B2A7B] focus:ring-1 focus:ring-[#1B2A7B]"
                data-testid="send-email-template-select"
              >
                {loading && <option>Loading templates…</option>}
                {!loading && templates.length === 0 && <option value="">No templates available</option>}
                {!loading && templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={loadTemplates}
                disabled={loading || sending}
                className="p-2 rounded-lg border border-slate-300 text-slate-500 hover:bg-slate-50"
                title="Reload templates from admin panel"
                data-testid="send-email-reload"
              >
                {loading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <RefreshCw className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              To
              {toSource === 'ota_support' && !toManuallyEdited && detectedOta && (
                <span
                  className="ml-2 text-[10px] font-normal text-[#1B2A7B]/70"
                  data-testid="send-email-to-source"
                >
                  · auto-filled from {detectedOta} support email
                </span>
              )}
            </label>
            <input
              type="email"
              value={to}
              onChange={(e) => { setTo(e.target.value); setToManuallyEdited(true); }}
              placeholder="guest@example.com"
              disabled={sending}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-[#1B2A7B] focus:ring-1 focus:ring-[#1B2A7B]"
              data-testid="send-email-to"
            />
          </div>

          {globalCc && (
            <div data-testid="send-email-cc-row">
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Cc
                <span className="ml-2 text-[10px] font-normal text-[#1B2A7B]/70">
                  · from BFR Email Settings
                </span>
              </label>
              <div
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-700 break-words"
                data-testid="send-email-cc"
                aria-readonly="true"
                title="Configured in booking-vault-admin — not editable here"
              >
                {globalCc}
              </div>
            </div>
          )}

          {globalBcc && (
            <div data-testid="send-email-bcc-row">
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Bcc
                <span className="ml-2 text-[10px] font-normal text-[#1B2A7B]/70">
                  · from BFR Email Settings
                </span>
              </label>
              <div
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-700 break-words"
                data-testid="send-email-bcc"
                aria-readonly="true"
                title="Configured in booking-vault-admin — not editable here"
              >
                {globalBcc}
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={resolving ? 'Resolving variables…' : 'Subject'}
              disabled={sending || resolving}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-[#1B2A7B] focus:ring-1 focus:ring-[#1B2A7B]"
              data-testid="send-email-subject"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Body</label>
            <textarea
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={resolving ? 'Resolving variables…' : 'Message'}
              disabled={sending || resolving}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono leading-relaxed focus:border-[#1B2A7B] focus:ring-1 focus:ring-[#1B2A7B]"
              data-testid="send-email-body"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Placeholders like <code>{'{{guest_name}}'}</code>, <code>{'{{check_in}}'}</code>, <code>{'{{amount}}'}</code>
              are auto-filled from the booking. Review and edit before sending.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 rounded-lg bg-white border border-slate-300 text-sm text-slate-700 hover:bg-slate-100"
            data-testid="send-email-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || resolving || !selectedId}
            className="px-4 py-2 rounded-lg bg-[#1B2A7B] text-white text-sm font-medium hover:bg-[#152163] disabled:opacity-60 inline-flex items-center gap-1.5"
            data-testid="send-email-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendEmailModal;
